import { RouteObject } from '@rocketlane/rocket-router';
import { Suspense, lazy, type ReactNode } from 'react';
import { Loading } from 'components/atoms/loading';
import { useAccountDataQuery } from 'queries/account';
import { NotFound } from '../404';
import { SecureRoute } from '../route-with-permission-check';
import { ROUTE_PATHS } from '../routes-paths';

// Lazy: this route is statically imported into `app-routes.tsx` via
// `analyticsAgentRoutes`, so any value imports here land in the app shell.
// The conversation page in particular drags @progress/kendo-react-pdf (~639 kB),
// highcharts (~281 kB), and the 307-module ai-analytics organism. Behind
// `import()` instead.
const AIAnalyticsPage = lazy(() =>
  import('components/pages/ai-analytics').then((m) => ({
    default: m.AIAnalyticsPage,
  })),
);
const AIAnalyticsConversationPage = lazy(() =>
  import('components/pages/ai-analytics').then((m) => ({
    default: m.AIAnalyticsConversationPage,
  })),
);

function AnalyticsAgentGate({ children }: { children: ReactNode }) {
  const { data, isLoading } = useAccountDataQuery();
  const isNitroAnalystEnabled =
    data?.accountMeta?.features?.nitroAnalyst ?? false;

  if (isLoading) {
    return <Loading />;
  }

  if (!isNitroAnalystEnabled) {
    return <NotFound />;
  }

  return <SecureRoute rule="AI_ANALYST:WRITE">{children}</SecureRoute>;
}

export const analyticsAgentRoutes: RouteObject[] = [
  {
    path: `${ROUTE_PATHS.AI_ANALYTICS}`,
    element: (
      <AnalyticsAgentGate>
        <Suspense fallback={<Loading />}>
          <AIAnalyticsPage />
        </Suspense>
      </AnalyticsAgentGate>
    ),
  },
  {
    path: `${ROUTE_PATHS.AI_ANALYTICS}/:conversationId`,
    element: (
      <AnalyticsAgentGate>
        <Suspense fallback={<Loading />}>
          <AIAnalyticsConversationPage />
        </Suspense>
      </AnalyticsAgentGate>
    ),
  },
];
